/**
 * PermissionsModal.jsx
 * Shown when admin accepts a join request — sets what the user can access.
 */
import React, { useState } from "react";

const PERMISSION_GROUPS = [
  {
    group: "Bookings", icon: "📋",
    permissions: [
      { key: "viewBookings",   label: "View Bookings",   desc: "Can see all bookings" },
      { key: "addBookings",    label: "Add Bookings",    desc: "Can add new bookings" },
      { key: "editBookings",   label: "Edit Bookings",   desc: "Can edit & change status" },
      { key: "deleteBookings", label: "Delete Bookings", desc: "Can permanently delete bookings" },
    ],
  },
  {
    group: "Properties", icon: "🏠",
    permissions: [
      { key: "manageProperties", label: "Manage Properties", desc: "Can add, edit and delete properties" },
    ],
  },
  {
    group: "Finance", icon: "💰",
    permissions: [
      { key: "viewFinance",    label: "View Finance",    desc: "Can see payment reminders & expenses" },
      { key: "manageExpenses", label: "Manage Expenses", desc: "Can add and delete expense entries" },
    ],
  },
  {
    group: "Reports", icon: "📜",
    permissions: [
      { key: "viewActivity", label: "View Activity Log", desc: "Can see the full audit trail" },
    ],
  },
];

const PRESETS = [
  { name: "View Only", icon: "👁️", desc: "View bookings only",
    color: "rgba(78,205,196,0.15)", border: "rgba(78,205,196,0.3)", textColor: "#4ecdc4",
    perms: { viewBookings: true } },
  { name: "Staff", icon: "👤", desc: "Add & view bookings",
    color: "rgba(167,139,250,0.15)", border: "rgba(167,139,250,0.3)", textColor: "#a78bfa",
    perms: { viewBookings: true, addBookings: true, viewFinance: true } },
  { name: "Manager", icon: "🧑‍💼", desc: "Full access except delete",
    color: "rgba(212,168,83,0.15)", border: "rgba(212,168,83,0.3)", textColor: "#d4a853",
    perms: { viewBookings: true, addBookings: true, editBookings: true,
             manageProperties: true, viewFinance: true, manageExpenses: true, viewActivity: true } },
  { name: "Full Access", icon: "🔓", desc: "Everything except admin",
    color: "rgba(81,207,102,0.12)", border: "rgba(81,207,102,0.3)", textColor: "#51cf66",
    perms: { viewBookings: true, addBookings: true, editBookings: true, deleteBookings: true,
             manageProperties: true, viewFinance: true, manageExpenses: true, viewActivity: true } },
];

const DEFAULT_PERMS = {
  viewBookings: true, addBookings: false, editBookings: false, deleteBookings: false,
  manageProperties: false, manageExpenses: false, viewFinance: false, viewActivity: false,
};

export default function PermissionsModal({ request, onConfirm, onCancel, loading }) {
  const [perms,        setPerms]        = useState({ ...DEFAULT_PERMS });
  const [activePreset, setActivePreset] = useState(null);

  const toggle = (key) => { setActivePreset(null); setPerms((p) => ({ ...p, [key]: !p[key] })); };
  const applyPreset = (preset) => { setActivePreset(preset.name); setPerms({ ...DEFAULT_PERMS, ...preset.perms }); };
  const selectedCount = Object.values(perms).filter(Boolean).length;

  return (
    <>
      <div onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 600, backdropFilter: "blur(6px)" }} />
      <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
        zIndex: 601, width: "min(560px,95vw)", maxHeight: "90vh", overflowY: "auto",
        background: "var(--bg-card)", border: "1px solid var(--border)",
        borderRadius: "var(--radius-xl)", boxShadow: "0 32px 100px rgba(0,0,0,0.7)", animation: "fadeIn 0.25s ease" }}>

        {/* Header */}
        <div style={{ padding: "22px 26px 18px", borderBottom: "1px solid var(--border)", background: "rgba(81,207,102,0.06)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <h2 style={{ fontSize: 20, marginBottom: 4 }}>🔐 Set Access Permissions</h2>
              <p style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                Accepting <strong style={{ color: "var(--accent-gold)" }}>{request.username}</strong> into <strong>{request.businessName}</strong>
              </p>
            </div>
            <button onClick={onCancel} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 20, padding: 0 }}>✕</button>
          </div>
        </div>

        <div style={{ padding: "20px 26px" }}>
          {/* Presets */}
          <p style={{ fontSize: 11, fontWeight: 700, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Quick Presets</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 22 }}>
            {PRESETS.map((preset) => (
              <button key={preset.name} onClick={() => applyPreset(preset)} style={{
                background: activePreset === preset.name ? preset.color : "var(--bg-secondary)",
                border: `1px solid ${activePreset === preset.name ? preset.border : "var(--border)"}`,
                borderRadius: 10, padding: "10px 14px", cursor: "pointer", textAlign: "left",
                transition: "all 0.15s", fontFamily: "var(--font-body)",
              }}>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2,
                  color: activePreset === preset.name ? preset.textColor : "var(--text-primary)" }}>
                  {preset.icon} {preset.name}
                </div>
                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{preset.desc}</div>
              </button>
            ))}
          </div>

          {/* Divider */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
            <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
            <span style={{ fontSize: 11, color: "var(--text-muted)" }}>or set manually</span>
            <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
          </div>

          {/* Permission toggles */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 22 }}>
            {PERMISSION_GROUPS.map((group) => (
              <div key={group.group}>
                <p style={{ fontSize: 11, fontWeight: 700, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
                  {group.icon} {group.group}
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {group.permissions.map((p) => {
                    const isOn = perms[p.key];
                    return (
                      <div key={p.key} onClick={() => toggle(p.key)} style={{
                        display: "flex", alignItems: "center", gap: 12, cursor: "pointer",
                        background: isOn ? "rgba(81,207,102,0.07)" : "var(--bg-secondary)",
                        border: `1px solid ${isOn ? "rgba(81,207,102,0.25)" : "var(--border)"}`,
                        borderRadius: 10, padding: "11px 14px", transition: "all 0.15s",
                      }}>
                        {/* Toggle */}
                        <div style={{ width: 40, height: 22, borderRadius: 99, flexShrink: 0,
                          background: isOn ? "var(--accent-green)" : "var(--bg-input)",
                          border: "1px solid " + (isOn ? "rgba(81,207,102,0.5)" : "var(--border)"),
                          position: "relative", transition: "background 0.2s" }}>
                          <div style={{ position: "absolute", top: 2, left: isOn ? 20 : 2,
                            width: 16, height: 16, borderRadius: "50%", background: "#fff",
                            transition: "left 0.2s", boxShadow: "0 1px 4px rgba(0,0,0,0.3)" }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <p style={{ fontSize: 13, fontWeight: 600, color: isOn ? "var(--text-primary)" : "var(--text-secondary)" }}>{p.label}</p>
                          <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>{p.desc}</p>
                        </div>
                        {isOn && <span style={{ color: "var(--accent-green)", fontSize: 16, flexShrink: 0 }}>✓</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)",
            borderRadius: 10, padding: "12px 16px", marginBottom: 20,
            display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 18 }}>📋</span>
            <div>
              <p style={{ fontSize: 13, fontWeight: 600 }}>
                {selectedCount === 0 ? "No permissions — user won't see anything"
                  : `${selectedCount} permission${selectedCount !== 1 ? "s" : ""} granted`}
              </p>
              <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>You can change these anytime from the Members section</p>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn" disabled={loading} onClick={() => onConfirm(perms)} style={{
              flex: 1, padding: "13px", fontSize: 14, fontWeight: 700,
              background: "rgba(81,207,102,0.18)", border: "1px solid rgba(81,207,102,0.4)",
              color: "var(--accent-green)",
            }}>
              {loading ? "Adding member…" : "✅ Accept & Grant Access"}
            </button>
            <button className="btn btn-ghost" onClick={onCancel} disabled={loading}
              style={{ padding: "13px 20px", fontSize: 14 }}>Cancel</button>
          </div>
        </div>
      </div>
    </>
  );
}
